import { useEffect, useMemo, useState } from 'react';
import { BrandWordmark } from '../components/BrandIdentity';
import WaitlistForm from './WaitlistForm';

function localDateString(date = new Date()) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

async function readJson(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'Operazione non riuscita');
  return payload;
}

function bookingSource() {
  const value = new URLSearchParams(window.location.search).get('src')?.toLowerCase();
  return ['website', 'instagram', 'google'].includes(value) ? value : 'direct';
}

export default function PublicBooking({ slug }) {
  const minDate = useMemo(() => localDateString(), []);
  const [restaurant, setRestaurant] = useState(null);
  const [date, setDate] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '', notes: '', privacyRead: false });
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    let active = true;
    fetch(`/api/booking/restaurant?slug=${encodeURIComponent(slug)}`).then(readJson)
      .then(({ restaurant: row }) => { if (active) setRestaurant(row); })
      .catch((requestError) => { if (active) setError(requestError.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  async function loadAvailability(event) {
    event.preventDefault();
    setError(''); setSelectedSlot(null); setLoadingSlots(true);
    try {
      const params = new URLSearchParams({ slug, date, partySize: String(partySize) });
      const payload = await readJson(await fetch(`/api/booking/availability?${params}`));
      setSlots(payload.slots || []);
      if (!payload.slots?.length) setError('Non ci sono orari disponibili per questa data. Prova un altro giorno.');
    } catch (requestError) {
      setSlots([]); setError(requestError.message);
    } finally { setLoadingSlots(false); }
  }

  async function confirmBooking(event) {
    event.preventDefault();
    if (!selectedSlot || !form.privacyRead) return;
    setSaving(true); setError('');
    try {
      const payload = await readJson(await fetch('/api/booking/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug, date, partySize, shiftId: selectedSlot.shiftId, startTime: selectedSlot.startTime,
          firstName: form.firstName, lastName: form.lastName, phone: form.phone,
          email: form.email, notes: form.notes, source: bookingSource(),
        }),
      }));
      setBooking(payload.booking);
    } catch (requestError) { setError(requestError.message); }
    finally { setSaving(false); }
  }

  if (loading) return <BookingShell><Loading /></BookingShell>;
  if (!restaurant) return <BookingShell><ErrorState message={error} /></BookingShell>;
  if (booking) return (
    <BookingShell>
      <div className="booking-success-icon success">✓</div>
      <p className="eyebrow">PRENOTAZIONE CONFERMATA</p>
      <h1>{booking.restaurantName}</h1>
      <p className="booking-lead">{booking.firstName}, ti aspettiamo <strong>{formatDate(booking.date)}</strong> alle <strong>{booking.startTime}</strong>, per {booking.partySize} {booking.partySize === 1 ? 'persona' : 'persone'}.</p>
      <a className="button full" href={booking.manageUrl}>Gestisci la prenotazione</a>
      <p className="booking-privacy">Conserva il collegamento per modificare o annullare la prenotazione.</p>
    </BookingShell>
  );

  return (
    <BookingShell>
      <p className="eyebrow">PRENOTAZIONE DIRETTA</p>
      <h1>{restaurant.name}</h1>
      <p className="booking-lead">{restaurant.intro || [restaurant.city, 'Scegli il tavolo in pochi secondi.'].filter(Boolean).join(' · ')}</p>
      <p><a href={`/menu/${encodeURIComponent(slug)}`}>Consulta menù e informazioni</a></p>
      {!restaurant.configured ? (
        <div className="booking-note">Le prenotazioni online saranno disponibili a breve. {restaurant.phone && <a href={`tel:${restaurant.phone}`}>Chiama il ristorante</a>}</div>
      ) : (
        <>
          <form className="booking-form" onSubmit={loadAvailability}>
            <div className="form-row booking-search-row">
              <label className="booking-date-field">Data<span className={`booking-date-control ${date ? '' : 'empty'}`}><span>{date ? formatCompactDate(date) : 'gg/mm/aaaa'}</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3v3m10-3v3M4.5 9h15M6 5h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" /></svg><input aria-label="Data" type="date" min={minDate} required value={date} onChange={(event) => { setDate(event.target.value); setSlots([]); setSelectedSlot(null); }} /></span></label>
              <label>Persone<input type="number" min="1" max={restaurant.maxPartySize || 50} required value={partySize} onChange={(event) => { setPartySize(Number(event.target.value)); setSlots([]); setSelectedSlot(null); }} /></label>
            </div>
            <button className="button full" disabled={loadingSlots || !date}>{loadingSlots ? 'Cerco gli orari…' : 'Mostra disponibilità'}</button>
          </form>
          {!!slots.length && (
            <div className="slot-section"><strong>Orari disponibili</strong><div className="slot-grid">{slots.map((slot) => {
              const selected = selectedSlot?.shiftId === slot.shiftId && selectedSlot?.startTime === slot.startTime;
              return <button type="button" className={selected ? 'selected' : ''} key={`${slot.shiftId}-${slot.startTime}`} onClick={() => { setSelectedSlot(slot); setError(''); }}><b>{slot.startTime}</b><small>{slot.shiftName}</small></button>;
            })}</div></div>
          )}
          {selectedSlot && (
            <form className="booking-form customer-form" onSubmit={confirmBooking}>
              <div className="selected-booking-summary"><span>{formatDate(date)}</span><strong>{selectedSlot.startTime} · {partySize} {partySize === 1 ? 'persona' : 'persone'}</strong></div>
              <div className="form-row">
                <label>Nome<input required maxLength="80" autoComplete="given-name" value={form.firstName} onChange={(event) => setForm((current) => ({ ...current, firstName: event.target.value }))} /></label>
                <label>Cognome<input required maxLength="80" autoComplete="family-name" value={form.lastName} onChange={(event) => setForm((current) => ({ ...current, lastName: event.target.value }))} /></label>
              </div>
              <label>Telefono<input required inputMode="tel" autoComplete="tel" placeholder="+39 333 123 4567" value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} /></label>
              <label>Email <small>Serve per ricevere la conferma e il link per modificare o annullare la prenotazione.</small><input required type="email" maxLength="254" autoComplete="email" placeholder="nome@email.it" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} /></label>
              <label>Note <small>(facoltative; non inserire dati sulla salute)</small><textarea maxLength="500" rows="3" placeholder="Indicazioni pratiche. Per allergie usa il modulo dedicato dopo la conferma o parla con lo staff." value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} /></label>
              <label className="privacy-check"><input type="checkbox" required checked={form.privacyRead} onChange={(event) => setForm((current) => ({ ...current, privacyRead: event.target.checked }))} /><span>Ho letto la <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>. I dati saranno usati esclusivamente per gestire la prenotazione.</span></label>
              <button className="button full" disabled={saving || !form.privacyRead}>{saving ? 'Conferma in corso…' : 'Conferma prenotazione'}</button>
            </form>
          )}
        </>
      )}
      {error && <div className="form-alert error" role="alert">{error}</div>}
      {restaurant.configured && date && <WaitlistForm key={`${date}:${partySize}`} slug={slug} date={date} partySize={partySize} />}
      <p className="booking-privacy">Prenotazione diretta gestita da RistoDaily. Nessun costo aggiuntivo per il cliente.</p>
    </BookingShell>
  );
}

export function BookingShell({ children }) {
  return <main className="booking-page direct-booking-page"><div className="booking-orb one" /><div className="booking-orb two" /><section className="booking-card glass-card"><a className="booking-brand" href="/"><BrandWordmark /><span className="booking-product">Prenota</span></a>{children}</section></main>;
}

function Loading() { return <div className="booking-loading"><span /><p>Prepariamo le disponibilità…</p></div>; }
function ErrorState({ message }) { return <><div className="booking-success-icon error">!</div><h1>Prenotazione non disponibile</h1><p className="booking-lead">{message || 'Contatta direttamente il ristorante.'}</p></>; }
function formatDate(date) { return new Date(`${date}T12:00:00`).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' }); }
function formatCompactDate(date) { return new Date(`${date}T12:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }); }
